import { alpha, createTheme, Theme, ThemeOptions } from '@mui/material';
import type { PaletteMode } from '@mui/material';

/**
 * Single source of truth for the admin panel's visual design.
 *
 * Everything that does not change between light and dark (typography, shape,
 * spacing, component overrides) is declared once in `sharedOptions`. Only the
 * palette differs per mode, so adding a colour in one mode can never silently
 * leave the other mode undefined.
 *
 * No component should contain a hex code. If a screen needs a colour that is
 * not here, it belongs here first.
 */

/** Brand colours, independent of mode. */
export const brand = {
  /** The PickupNinja orange — the one accent, everywhere. */
  orange: '#FF6B35',
  orangeLight: '#FF8A65',
  orangeDark: '#E64A19',
  /** The near-black used for the navigation rail and primary text. */
  ink: '#16181D',
  inkLight: '#242830',
  inkMuted: '#8A8F98',
} as const;

/**
 * Design tokens that are not part of MUI's palette but must still have exactly
 * one definition. Imported directly where `theme.palette` cannot reach.
 */
export const tokens = {
  radius: {
    /** Pills, chips, segmented controls. */
    full: 999,
    /** Inputs, buttons, small controls. */
    control: 12,
    /** Cards, panels, dialogs. */
    surface: 16,
  },
  /** The fixed navigation rail, expanded and collapsed. */
  sidebar: {
    width: 264,
    collapsedWidth: 76,
  },
  /** Height of the sticky top bar. */
  topBarHeight: 68,
  /**
   * Below this breakpoint the sidebar becomes a temporary drawer and data
   * tables render as stacked cards. One value so the shell and the tables
   * cannot disagree about what "mobile" means.
   */
  mobileBreakpoint: 'md' as const,
} as const;

const semantic = {
  error: '#E5484D',
  warning: '#F5A524',
  info: '#3E7BFA',
  success: '#2FB344',
} as const;

/** Brighter semantic colours so they stay legible on a dark ground. */
const semanticDark = {
  error: '#FF8A8E',
  warning: '#FFC266',
  info: '#7BA6FF',
  success: '#5FD675',
} as const;

/**
 * Frosted-glass surface, keyed by mode.
 *
 * Glass reads as premium against a backdrop that actually has depth to show
 * through it — which is why this is dark-mode's signature and light mode
 * keeps a flat, opaque surface instead of a muddy tint. Spread this into an
 * `sx` prop; it is deliberately not a full component override, because a few
 * surfaces (the nav rail, dialogs) want the plain palette colour, not glass.
 *
 * `backdropFilter: blur()` is unsupported in a handful of older embedded
 * browsers (old in-app WebViews); every consumer falls back to the solid
 * `background` colour underneath, which alone is a perfectly normal card.
 */
export function glassSurface(mode: PaletteMode): Record<string, any> {
  if (mode === 'dark') {
    return {
      backgroundColor: alpha('#1D2028', 0.6),
      backgroundImage: 'none',
      backdropFilter: 'blur(20px) saturate(140%)',
      WebkitBackdropFilter: 'blur(20px) saturate(140%)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 1px 0 0 rgba(255,255,255,0.06) inset, 0 8px 30px rgba(0,0,0,0.35)',
    };
  }
  return {
    backgroundColor: alpha('#FFFFFF', 0.72),
    backgroundImage: 'none',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    border: '1px solid rgba(22,24,29,0.06)',
    boxShadow: '0 1px 0 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(22,24,29,0.06)',
  };
}

/**
 * The warm ambient glow behind hero panels — the soft amber bloom the
 * reference design bleeds in from one corner. A large, heavily-blurred radial
 * gradient positioned absolutely behind a `position: relative` container;
 * z-index 0 so real content (z-index 1+) sits above it undisturbed. Visible
 * only where it has a dark ground to glow against.
 */
export function heroGlow(mode: PaletteMode): Record<string, any> {
  if (mode !== 'dark') return {};
  return {
    position: 'absolute',
    inset: '-40% -20% auto auto',
    width: '70%',
    height: '160%',
    background: `radial-gradient(closest-side, ${alpha(brand.orange, 0.35)}, transparent 70%)`,
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  };
}

function paletteFor(mode: PaletteMode): ThemeOptions['palette'] {
  if (mode === 'dark') {
    return {
      mode,
      primary: {
        // On a dark ground the brand ink has no contrast, so the accent
        // orange carries primary actions and selection.
        main: brand.orange,
        light: brand.orangeLight,
        dark: brand.orangeDark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#A6ADBB',
        light: '#CDD3DC',
        dark: '#6B7280',
        contrastText: brand.ink,
      },
      background: {
        // A flat colour under the ambient radial glow layered on by
        // MuiCssBaseline below — this is the fallback for anything that
        // reads the palette directly rather than sitting on that layer.
        default: '#0B0C10',
        paper: '#15171D',
      },
      text: {
        primary: '#F2F3F6',
        secondary: '#9BA1AC',
      },
      divider: 'rgba(255, 255, 255, 0.10)',
      error: { main: semanticDark.error },
      warning: { main: semanticDark.warning },
      info: { main: semanticDark.info },
      success: { main: semanticDark.success },
    };
  }

  return {
    mode,
    primary: {
      main: brand.orange,
      light: brand.orangeLight,
      dark: brand.orangeDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.ink,
      light: brand.inkLight,
      dark: '#0B0D11',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F6F7F9',
      paper: '#FFFFFF',
    },
    text: {
      primary: brand.ink,
      secondary: brand.inkMuted,
    },
    divider: 'rgba(22, 24, 29, 0.08)',
    error: { main: semantic.error },
    warning: { main: semantic.warning },
    info: { main: semantic.info },
    success: { main: semantic.success },
  };
}

/**
 * Headings and numerals carry Plus Jakarta Sans — a geometric, slightly
 * rounded display face with real character at large sizes — while body copy
 * and dense table text stay on Inter, which is tuned for legibility at small
 * sizes. Both are loaded in public/index.html; neither was previously linked
 * anywhere, so every screen had silently been rendering in the OS default
 * sans the whole time.
 */
const displayFont = '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const bodyFont = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

const sharedOptions: ThemeOptions = {
  typography: {
    fontFamily: bodyFont,
    h1: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontFamily: displayFont, fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600 },
    // Secondary metadata throughout the admin reads at this size.
    caption: { fontSize: '0.75rem', letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: tokens.radius.control,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme: Theme) => ({
        // Long tables and wide dialogs must never make the page itself scroll
        // sideways on a phone; the container scrolls instead.
        body: {
          overflowX: 'hidden',
          ...(theme.palette.mode === 'dark'
            ? {
                // The ambient warm glow the reference design bathes every
                // panel in — two soft radial blooms, fixed to the viewport
                // so they read as room lighting rather than scrolling with
                // the page.
                backgroundColor: theme.palette.background.default,
                backgroundImage: [
                  `radial-gradient(1100px circle at 85% -10%, ${alpha(brand.orange, 0.16)}, transparent 55%)`,
                  `radial-gradient(900px circle at -10% 60%, ${alpha(brand.orange, 0.08)}, transparent 55%)`,
                ].join(', '),
                backgroundAttachment: 'fixed',
              }
            : {}),
        },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: tokens.radius.control,
          // Comfortable tap target on touch devices.
          minHeight: 40,
          paddingInline: 16,
        },
        sizeSmall: { minHeight: 34, paddingInline: 12 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.control },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.full, fontWeight: 600 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: tokens.radius.surface,
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'none'
              : '0 1px 2px rgba(22, 24, 29, 0.04)',
          ...(theme.palette.mode === 'dark' ? glassSurface('dark') : {}),
          // A Card nested inside a Dialog sits on the Dialog paper's OWN
          // blur, which already stands between it and any real page content
          // — there is nothing further back for a second blur to pick up,
          // only the dialog's already-frosted surface. Stacking backdrop-
          // filter contexts like that is pure rendering cost for no visible
          // gain, and a known fragility point in a couple of older WebViews.
          // The nested card gets a solid-ish tint instead: still reads as
          // "a surface sitting on the glass," with none of the nested-blur
          // risk.
          ...(theme.palette.mode === 'dark' && {
            // Only the blur is dropped here, not the background colour: a
            // page that gives one of these cards its own tone (a coloured
            // stat tile, for instance) sets bgcolor directly via `sx`, and
            // that must keep winning. Only glassSurface's OWN translucent
            // fallback backgroundColor needs replacing with something solid,
            // which happens below rather than in this selector.
            '.MuiDialog-paper &': {
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            },
          }),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: tokens.radius.surface },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.control },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.palette.divider,
        }),
        head: ({ theme }: { theme: Theme }) => ({
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: theme.palette.text.secondary,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.02)'
              : 'rgba(22,24,29,0.02)',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }: { theme: Theme }) => ({
          borderRadius: tokens.radius.surface,
          backgroundImage: 'none',
          ...(theme.palette.mode === 'dark'
            ? {
                backgroundColor: alpha('#15171D', 0.92),
                backdropFilter: 'blur(24px) saturate(140%)',
                WebkitBackdropFilter: 'blur(24px) saturate(140%)',
                border: '1px solid rgba(255,255,255,0.08)',
              }
            : {}),
          // Dialogs go full-bleed on phones rather than floating in a sliver.
          [theme.breakpoints.down('sm')]: {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: 0,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 8, fontSize: '0.75rem' },
      },
    },
  },
};

/** Builds the theme for a given mode. */
export function createAppTheme(mode: PaletteMode): Theme {
  return createTheme({
    ...sharedOptions,
    palette: paletteFor(mode),
  });
}
